import React, { useState } from "react";
import SiteLayout from "../components/SiteLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Clock, ArrowUpRight } from "lucide-react";

const TYPES = [
  { v: "contact", l: "General inquiry" },
  { v: "quote", l: "Custom quote" },
  { v: "consultation", l: "Free consultation" },
  { v: "strategy_call", l: "Strategy call" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", type: "contact" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Got it! We'll reply within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", message: "", type: "contact" });
    } catch {
      toast.error("Couldn't send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <section className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">Get in touch</div>
              <h1 className="font-display text-5xl font-bold tracking-tight">Let&apos;s talk strategy.</h1>
              <p className="mt-5 text-inkSoft text-lg leading-relaxed">Custom quotes, partner pricing, agency onboarding, bulk link building — we reply to every message within one business day.</p>
              <div className="mt-10 space-y-5">
                <div className="flex items-center gap-3 text-sm">
                  <div className="h-10 w-10 rounded-lg bg-surfaceAlt grid place-items-center text-terra"><Mail size={16} /></div>
                  <div><div className="font-medium">zenithranking27@gmail.com</div><div className="text-inkSoft text-xs">For inquiries, cancellations, partnerships & support</div></div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="h-10 w-10 rounded-lg bg-surfaceAlt grid place-items-center text-terra"><Clock size={16} /></div>
                  <div><div className="font-medium">Mon–Fri · 9am–6pm GMT</div><div className="text-inkSoft text-xs">Avg response · 4 hours</div></div>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="lg:col-span-7 card-soft p-7 sm:p-10 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-inkSoft">Inquiry type</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.v}
                      onClick={() => setForm({ ...form, type: t.v })}
                      className={`tag transition ${form.type === t.v ? "bg-ink text-white border-ink" : ""}`}
                      data-testid={`contact-type-${t.v}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-inkSoft">Message</label>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink"
                  data-testid="contact-message"
                  placeholder="Tell us about your goals..."
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="contact-submit">
                {loading ? "Sending..." : (<>Send message <ArrowUpRight size={16} /></>)}
              </button>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-inkSoft">{label}{required && " *"}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
        data-testid={testid}
      />
    </label>
  );
}
